// src/components/rcv/asociaciones/DocumentosList.jsx
import React from 'react';
import {
  Paper, Typography, Divider, Box, CircularProgress,
  List, ListItemButton, ListItemText, Chip, Tooltip
} from '@mui/material';
import { formatearRut, formatearMonto, getTipoDocLabel } from '../../../utils/formatters';
import DocumentoButton from '../DocumentoButton';

const DocumentosList = ({
  documentos,
  mapaAsociaciones,
  documentoSeleccionado,
  setDocumentoSeleccionado,
  loading,
  mostrarSoloNoAsociados,
  totalDocumentos,
  conAdjunto = new Set(),
  rutEmpresa,
  onDocumentoCreado
}) => {
  // Función para calcular cuántos documentos están pendientes de asociar
  const calcularDocumentosPendientes = () => {
    return documentos.filter(doc => {
      const key = `${doc.rut_proveedor}-${doc.folio}`;
      return !mapaAsociaciones[key];
    }).length;
  };

  // Componente para mostrar info del documento
  const DocumentoSecundario = ({ doc }) => (
    <span>
      <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={doc.razon_social}>
        {doc.razon_social}
      </span>
      <span style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        <span style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
          {formatearRut(doc.rut_proveedor, doc.dv_proveedor)}
        </span>
        <span style={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)' }}>
          Folio: {doc.folio}
        </span>
      </span>
    </span>
  );

  return (
    <Paper sx={{ height: '500px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <Typography variant="subtitle1" fontWeight="medium">
          Documentos del Período
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            {documentos.length} documentos encontrados
          </Typography>
          {mostrarSoloNoAsociados ? (
            <Typography variant="caption" color="primary">
              Mostrando solo pendientes de asociar
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {calcularDocumentosPendientes()} pendientes de {totalDocumentos} totales
            </Typography>
          )}
        </Box>
      </Box>
      
      <Divider />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <CircularProgress size={40} />
        </Box>
      ) : documentos.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No hay documentos para el período seleccionado.
          </Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ flexGrow: 1, overflow: 'auto' }}>
          {documentos.map((doc, index) => {
            const key = `${doc.rut_proveedor}-${doc.folio}`;
            const tieneAsociacion = !!mapaAsociaciones[key];
            const esSeleccionado = documentoSeleccionado && 
                                documentoSeleccionado.folio === doc.folio && 
                                documentoSeleccionado.rut_proveedor === doc.rut_proveedor;
            
            return (
              <React.Fragment key={`${doc.rut_proveedor}-${doc.folio}-${index}`}>
                <ListItemButton 
                  selected={esSeleccionado}
                  onClick={() => setDocumentoSeleccionado(doc)}
                  sx={{
                    borderLeft: '4px solid',
                    borderLeftColor: tieneAsociacion ? 'success.main' : 'warning.main',
                    backgroundColor: tieneAsociacion ? 'rgba(76, 175, 80, 0.08)' : 'inherit'
                  }}
                >
                  <ListItemText
                    primary={
                      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          label={getTipoDocLabel(doc.tipo_doc)} 
                          size="small" 
                          variant="outlined"
                          color={tieneAsociacion ? "success" : "default"}
                        />
                        <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {formatearMonto(doc.monto_total)}
                        </span>
                      </span>
                    }
                    secondary={<DocumentoSecundario doc={doc} />}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title={tieneAsociacion ? "Modificar asociación" : "Asociar documento"}>
                      <Chip
                        size="small"
                        label={tieneAsociacion ? "Asociado" : "Pendiente"}
                        color={tieneAsociacion ? "success" : "warning"}
                        variant={tieneAsociacion ? "filled" : "outlined"}
                        sx={{ ml: 1 }}
                      />
                    </Tooltip>
                    
                    {/* Botón para visualizar/cargar documento */}
                    <DocumentoButton
                      soloIcono={true}
                      movimientoId={doc.id}
                      rutEmisor={doc.rut_proveedor}
                      tipoDoc={doc.tipo_doc}
                      folio={doc.folio}
                      rutEmpresa={rutEmpresa}
                      tieneAdjunto={conAdjunto.has(doc.id)}
                      onDocumentoCreado={onDocumentoCreado}
                    />
                  </Box>
                </ListItemButton>
                <Divider />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default DocumentosList;